import type {
  IntegrationKind,
  McpConfig,
  McpOAuthSettings,
} from '@/features/Integrations/types/integration';

/**
 * Tipo da integração (AB#87477). O template traz `kind` pronto no DTO; a instância herda
 * pela cópia do `mcp_config`. Ausente nos dois = `api`, que é tudo o que existia antes.
 */
export function templateKind(template: {
  kind?: IntegrationKind | null;
  mcp_config?: McpConfig | null;
}): IntegrationKind {
  if (template.kind === 'mcp' || template.kind === 'api') return template.kind;
  return template.mcp_config?.kind === 'mcp' ? 'mcp' : 'api';
}

export function instanceKind(base: { mcp_config?: McpConfig | null }): IntegrationKind {
  return base.mcp_config?.kind === 'mcp' ? 'mcp' : 'api';
}

/** `settings.mcp_oauth` da instância, quando ela é um servidor MCP com OAuth. */
export function mcpOAuthOf(base: { settings?: Record<string, unknown> | null }): McpOAuthSettings | null {
  const value = base.settings?.mcp_oauth;
  if (!value || typeof value !== 'object') return null;
  const status = (value as { status?: unknown }).status;
  return status === 'pending' || status === 'connected' || status === 'reconnect'
    ? (value as McpOAuthSettings)
    : null;
}

/**
 * Template que uma organização trouxe e o time do Prodgy ainda não revisou: a api só o mostra
 * para ela (e ao superadmin). Seed e antigos: ausente = publicado.
 */
export function isPendingReview(template: { review_status?: string | null }): boolean {
  return template.review_status === 'pending';
}

interface CatalogTemplate {
  label: string;
  kind?: IntegrationKind | null;
  mcp_config?: McpConfig | null;
  review_status?: string | null;
  active?: boolean;
  settings?: { block?: boolean } | null;
}

export interface CatalogFilter<T> {
  kind: IntegrationKind;
  query?: string;
  /** Descrição no idioma da tela — a busca procura nela além do label. */
  describe?: (template: T) => string | null | undefined;
}

/**
 * O que o seletor lista para um filtro: só o tipo escolhido, sem os bloqueados
 * (`settings.block`), com a busca no label e na descrição, ativos antes dos inativos.
 *
 * Várias instâncias do mesmo template são permitidas (AB#87477): já instalado não tira
 * nada da lista.
 */
export function filterCatalog<T extends CatalogTemplate>(
  templates: readonly T[],
  { kind, query, describe }: CatalogFilter<T>,
): T[] {
  const needle = (query ?? '').trim().toLowerCase();
  const filtered = templates.filter((template) => {
    if (template.settings?.block === true) return false;
    if (templateKind(template) !== kind) return false;
    if (!needle) return true;
    const description = describe?.(template) ?? '';
    return (
      template.label.toLowerCase().includes(needle) ||
      description.toLowerCase().includes(needle)
    );
  });
  // sort estável: ativos primeiro, a ordem do backend se mantém dentro de cada grupo.
  return [...filtered].sort((a, b) => {
    const aActive = a.active !== false;
    const bActive = b.active !== false;
    if (aActive === bActive) return 0;
    return aActive ? -1 : 1;
  });
}
